import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import HelpProgressBar from './HelpProgressBar';

const CARD_WIDTH = 320;
const MARGIN = 16;
const ARROW_SIZE = 8;

function calculatePosition(targetRect, preferredPosition, cardHeight) {
    if (!targetRect) return { top: 0, left: 0, arrow: null };

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cardW = Math.min(CARD_WIDTH, vw - MARGIN * 2);

    const positions = [preferredPosition, 'bottom', 'top', 'right', 'left'];

    for (const pos of positions) {
        let top, left;
        const centerX = targetRect.left + targetRect.width / 2 - cardW / 2;
        const clampedLeft = Math.max(MARGIN, Math.min(centerX, vw - cardW - MARGIN));

        if (pos === 'bottom') {
            top = targetRect.bottom + MARGIN;
            left = clampedLeft;
            if (top + cardHeight < vh) return { top, left, arrow: 'top' };
        } else if (pos === 'top') {
            top = targetRect.top - cardHeight - MARGIN;
            left = clampedLeft;
            if (top > 0) return { top, left, arrow: 'bottom' };
        } else if (pos === 'right') {
            top = targetRect.top + targetRect.height / 2 - cardHeight / 2;
            left = targetRect.right + MARGIN;
            top = Math.max(MARGIN, Math.min(top, vh - cardHeight - MARGIN));
            if (left + cardW < vw) return { top, left, arrow: 'left' };
        } else if (pos === 'left') {
            top = targetRect.top + targetRect.height / 2 - cardHeight / 2;
            left = targetRect.left - cardW - MARGIN;
            top = Math.max(MARGIN, Math.min(top, vh - cardHeight - MARGIN));
            if (left > 0) return { top, left, arrow: 'right' };
        }
    }

    // Fallback: center of screen
    return {
        top: vh / 2 - cardHeight / 2,
        left: vw / 2 - CARD_WIDTH / 2,
        arrow: null,
    };
}

function getArrowStyle(direction, targetRect, cardTop, cardLeft, cardWidth, cardHeight) {
    if (!direction || !targetRect) return null;

    const targetCenterX = targetRect.left + targetRect.width / 2;
    const targetCenterY = targetRect.top + targetRect.height / 2;

    if (direction === 'top') {
        const arrowX = Math.max(20, Math.min(targetCenterX - cardLeft, cardWidth - 20));
        return { top: -ARROW_SIZE, left: arrowX, transform: 'translateX(-50%) rotate(45deg)' };
    } else if (direction === 'bottom') {
        const arrowX = Math.max(20, Math.min(targetCenterX - cardLeft, cardWidth - 20));
        return { bottom: -ARROW_SIZE, left: arrowX, transform: 'translateX(-50%) rotate(45deg)' };
    } else if (direction === 'left') {
        const arrowY = Math.max(20, Math.min(targetCenterY - cardTop, cardHeight - 20));
        return { left: -ARROW_SIZE, top: arrowY, transform: 'translateY(-50%) rotate(45deg)' };
    } else if (direction === 'right') {
        const arrowY = Math.max(20, Math.min(targetCenterY - cardTop, cardHeight - 20));
        return { right: -ARROW_SIZE, top: arrowY, transform: 'translateY(-50%) rotate(45deg)' };
    }
    return null;
}

const HelpStepCard = ({ step, stepIndex, totalSteps, targetRect, onNext, onPrev, onClose }) => {
    const { language } = useLanguage();
    const cardRef = useRef(null);
    const [isEntering, setIsEntering] = useState(true);
    const cardHeightRef = useRef(200);

    // Measure card height after first paint of each step (one-shot, no loop)
    useEffect(() => {
        const timer = setTimeout(() => {
            if (cardRef.current) {
                cardHeightRef.current = cardRef.current.offsetHeight;
            }
        }, 50);
        return () => clearTimeout(timer);
    }, [stepIndex]);

    // Entrance animation
    useEffect(() => {
        setIsEntering(true);
        const timer = setTimeout(() => setIsEntering(false), 50);
        return () => clearTimeout(timer);
    }, [stepIndex]);

    const position = calculatePosition(targetRect, step?.position || 'bottom', cardHeightRef.current);

    const handleNext = useCallback(() => {
        if (stepIndex >= totalSteps - 1) {
            onClose();
        } else {
            onNext();
        }
    }, [stepIndex, totalSteps, onClose, onNext]);

    if (!step) return null;

    // Compute arrow style inline (no state needed)
    const arrowStyle = getArrowStyle(
        position.arrow,
        targetRect,
        position.top,
        position.left,
        Math.min(CARD_WIDTH, window.innerWidth - MARGIN * 2),
        cardHeightRef.current
    );

    return (
        <div
            ref={cardRef}
            className={`
                fixed z-[310] pointer-events-auto
                w-80 max-w-[calc(100vw-2rem)]
                bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl
                rounded-2xl border border-white/30 dark:border-white/10
                shadow-2xl shadow-blue-500/10 dark:shadow-blue-400/5
                p-5
                transition-all duration-300 ease-out
                ${isEntering ? 'opacity-0 translate-y-2 scale-95' : 'opacity-100 translate-y-0 scale-100'}
            `}
            style={{ top: position.top, left: position.left }}
        >
            {/* Animated gradient shimmer */}
            <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-blue-500/10 opacity-50 animate-gradient-shift" />
            </div>

            {/* Arrow */}
            {arrowStyle && (
                <div
                    className="absolute w-4 h-4 bg-white/90 dark:bg-gray-900/90 border border-white/30 dark:border-white/10"
                    style={arrowStyle}
                />
            )}

            {/* Step counter and close button */}
            <div className="flex items-center justify-between mb-3 relative">
                <span className="text-[10px] font-black uppercase tracking-widest text-blue-600 dark:text-blue-400">
                    {stepIndex + 1} / {totalSteps}
                </span>
                <button
                    onClick={onClose}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition"
                    aria-label="Close"
                >
                    <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            {/* Title */}
            <h3 className="text-sm font-black text-gray-900 dark:text-white mb-2 relative">
                {step.title[language]}
            </h3>

            {/* Description */}
            <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed mb-4 relative">
                {step.description[language]}
            </p>

            {/* Progress bar */}
            <div className="relative">
                <HelpProgressBar current={stepIndex} total={totalSteps} />
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-4 relative">
                <button
                    onClick={onPrev}
                    disabled={stepIndex === 0}
                    className="text-xs font-bold text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed transition"
                >
                    {language === 'fr' ? 'Précédent' : 'Previous'}
                </button>

                <button
                    onClick={handleNext}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl transition shadow-lg shadow-blue-500/20 active:scale-95"
                >
                    {stepIndex === totalSteps - 1
                        ? (language === 'fr' ? 'Terminer ✓' : 'Finish ✓')
                        : (language === 'fr' ? 'Suivant →' : 'Next →')
                    }
                </button>
            </div>
        </div>
    );
};

export default HelpStepCard;
